/**
 * DialogFieldNode - Router component for dialog field nodes
 * 
 * Routes to the appropriate node component based on field type.
 * Maintains backward compatibility with existing flow configuration.
 */

import React, { memo } from 'react';
import { NodeProps } from '@xyflow/react';
import { DialogFieldNodeData } from './dialog/dialogNodeUtils';
import { DialogTextNode } from './dialog/DialogTextNode';
import { QuestionNode } from './dialog/QuestionNode';
import { ContextNode } from './dialog/ContextNode';
import { ConditionNode } from './dialog/ConditionNode';
import { SetterNode } from './dialog/SetterNode';
import { EndTextNode } from './dialog/EndTextNode';
import { EndDialogNode } from './dialog/EndDialogNode';

/**
 * Router component that delegates to the appropriate node based on field type
 */
export const DialogFieldNode = memo((props: NodeProps<any>) => {
  const nodeData = props.data as DialogFieldNodeData;
  const fieldType = nodeData.field?.type;

  switch (fieldType) {
    case 'question':
      return <QuestionNode {...props} />;
    case 'context':
      return <ContextNode {...props} />;
    case 'condition':
      return <ConditionNode {...props} />;
    case 'setter':
      return <SetterNode {...props} />;
    case 'end-text': // Legacy type, redirect to end-dialog
    case 'end-dialog':
      return <EndDialogNode {...props} />;
    case 'dialog':
    default:
      return <DialogTextNode {...props} />;
  }
});

DialogFieldNode.displayName = 'DialogFieldNode';

export default DialogFieldNode;

// Re-export individual components for direct use
export { DialogTextNode } from './dialog/DialogTextNode';
export { QuestionNode } from './dialog/QuestionNode';
export { ContextNode } from './dialog/ContextNode';
export { ConditionNode } from './dialog/ConditionNode';
export { SetterNode } from './dialog/SetterNode';
export { EndTextNode } from './dialog/EndTextNode';
export { EndDialogNode } from './dialog/EndDialogNode';
export { BaseDialogNode } from './dialog/BaseDialogNode';
export * from './dialog/dialogNodeUtils';
